import * as React from 'react';
import {
  Box, Typography, IconButton, Tooltip, Divider, TextField, InputAdornment,
  Stack, Button, Popover, MenuItem, Select, FormControl, InputLabel, Chip, Checkbox,
  Menu, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete
} from '@mui/material';
import {
  CloseOutlined, LocalShippingOutlined, LowPriorityOutlined, StopCircleOutlined,
  Search as SearchIcon, FilterList as FilterListIcon, Clear as ClearIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon, DoneAll as DoneAllIcon, SelectAll as SelectAllIcon,
  KeyboardArrowRightOutlined,
  WarningAmberOutlined
} from '@mui/icons-material';
import {
  Timeline, TimelineItem, TimelineSeparator, TimelineContent, TimelineDot,
} from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { Theme, alpha } from '@mui/material/styles';
import { useDraggable } from '@dnd-kit/core';
import Fuse from 'fuse.js';
import { type RuleGroupType, type Field } from 'react-querybuilder';
import jsonLogic, { RulesLogic } from 'json-logic-js';

/* ---------- Types (same as before) ---------- */
export type Address = {
  country?: string | null;
  city?: string;
  postal_code?: string;
  street?: string;
  house_number?: string | number | null;
} | null;

export type UnplannedShipment = {
  custom_id?: string;
  customer_number?: string | number | null;
  customer_name?: string;
  address?: Address;
  priority?: number | string | null;
  type?: 'pickup' | 'delivery' | string | null;
  location?: { address?: Address } | null;
  calculated_properties?: {
    load_classes?: string | string[] | null;
    stop_unit_load_classes?: string | string[] | null;
  };
  loads?: number[] | null;
  category?: string | null;
  reason?: string | null;
};

export interface UnplannedShipmentsTimelineProps {
  shipments: UnplannedShipment[];
  title?: string;
  maxHeight?: number;
  onSelect?: (shipment: UnplannedShipment, index: number) => void;
  onClose?: () => void;
  dimensionLabels?: { dimension?: string; unit?: string }[];
  onFilteringChange?: (active: boolean, count?: number) => void;
  onAssignShipments?: (ids: string[], vehicleId: string) => void;
  routeIds?: string[];
  onReadyToAssign?: () => void;
  routesHidden?: boolean;
  dragEnabled?: boolean;
  assignedIds?: Set<string>;
}

/* ---------- Helpers (same) ---------- */
const formatAddress = (address?: Address): string => {
  if (!address || typeof address !== 'object') return '';
  const street = [address.street, address.house_number].filter(Boolean).join(' ').trim();
  const locality = [address.postal_code, address.city].filter(Boolean).join(' ').trim();
  const country = address.country || '';
  return [street, locality, country].filter(Boolean).join(', ');
};
const getTitle = (s: UnplannedShipment, i: number) =>
  s.customer_name?.trim() || s.custom_id?.trim() || `Shipment ${i + 1}`;
const pickAddress = (s: UnplannedShipment): Address => s.address ?? s.location?.address ?? null;
const getLoadClassesText = (s: UnplannedShipment): string => {
  const value =
    s.calculated_properties?.load_classes ??
    s.calculated_properties?.stop_unit_load_classes ??
    (s as any).load_class ?? null;
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return (value ?? '').toString().trim();
};
const sumLoads = (s: UnplannedShipment) =>
  (Array.isArray(s.loads) ? s.loads : []).reduce((a, b) => a + (Number(b) || 0), 0);

/* ---------- Styles ---------- */
type StyleProps = { maxHeight?: number; clickable?: boolean };
const DOT = { default: '#ccc', pickup: '#ef5350', delivery: '#0288d1' };

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: { height: '100%', display: 'flex', flexDirection: 'column' },
  header: {
    position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center',
    padding: '6px 10px', gap: theme.spacing(1), backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  grow: { flexGrow: 1, fontSize: '16px' },
  controls: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px',
    background: theme.palette.background.paper, position: 'sticky', top: 41, zIndex: 1,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  scroll: { overflowY: 'auto', padding: '16px 0px 16px 15px' },
  textLine: { marginBottom: 0 },
  emptyText: { padding: theme.spacing(1) },
  timelineRoot: {
    margin: 0, padding: 0,
    '& .MuiTimelineItem-root:before': { flex: 0, padding: 0 },
  },
  item: (p) => ({
    cursor: p.clickable ? 'pointer' : 'context-menu',
    '&:before': { flex: 0, padding: 0 }
  }),
  content: { paddingTop: theme.spacing(1), paddingBottom: theme.spacing(1), paddingLeft: theme.spacing(2) },
  dotBase: { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dotDefault: { backgroundColor: DOT.default, boxShadow: `0 0 0 8px ${alpha(DOT.default, 0.26)}` },
  dotPickup: { backgroundColor: DOT.pickup, boxShadow: `0 0 0 8px ${alpha(DOT.pickup, 0.26)}` },
  dotDelivery: { backgroundColor: DOT.delivery, boxShadow: `0 0 0 8px ${alpha(DOT.delivery, 0.26)}` },
  dotMixed: { background: `linear-gradient(135deg, ${DOT.pickup} 50%, ${DOT.delivery} 50%)`, boxShadow: '0 0 0 8px rgba(128,0,128,0.26)' },
  dotIcon: { color: '#fff' },
  textSmall: { fontWeight: 400, fontSize: '13px' },
  priorityRow: { display: 'flex', alignItems: 'center', columnGap: theme.spacing(2), fontSize: '13px' },
  priorityItem: { display: 'flex', alignItems: 'center' },
  priorityIcon: { marginRight: 4 },
  loadIcon: { fontSize: '20px' },
  loadsRow: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5), alignItems: 'center', marginTop: theme.spacing(0.5) },
  loadItem: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(0.5) },
  loadLabel: { fontSize: 13, fontWeight: 400, color: 'rgb(128, 128, 128)' },
  loadValue: { fontSize: 13, fontWeight: 500 },
  chipsRow: { gap: 8, padding: '6px 10px', borderBottom: `1px dashed ${theme.palette.divider}` },
  ctxList: {
    padding: 0, // compact list
  },
  ctxItem: {
    padding: '10px',
    fontSize: 16,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  ctxChevron: {
    fontSize: 24,
    color: theme.palette.text.secondary,
    marginLeft: 28,
  },
  closeIcon: {
    color: '#000000'
  },
  dialogPaper: {
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: theme.shadows[6],
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 7,
      backgroundColor: '#ef5927',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '26px 9px 10px 30px',
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  diamondBox: {
    width: 32,
    height: 32,
    backgroundColor: '#ef5927',
    boxShadow: '0 0 0 8px rgba(239, 89, 39, 0.4);',
    borderRadius: 4,
    transform: 'rotate(45deg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warningIcon: {
    transform: 'rotate(-45deg)',
    color: '#ffffff',
    fontSize: 20,
  },
  titleText: {
    fontWeight: 700,
    marginLeft: '16px'
  },
  dialogContent: {
    '&.MuiDialogContent-root': {
      padding: '16px 85px 10px',
    },
  },
  contentPrimary: {
    color: theme.palette.text.primary,
  },
  contentSecondary: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  dividerTop: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
}));

/* ---------- Debounce ---------- */
function useDebounced<T>(value: T, delay = 250) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}

/* ---------- Filter doc ---------- */
type FilterDoc = {
  idHint: string;
  customerName: string;
  city: string;
  postalCode: string;
  street: string;
  priorityNum: number | null;
  type: string;
  loadClasses: string;
  loadsTotal: number;
  category: string;
  reason: string;
};
function toFilterDoc(s: UnplannedShipment, i: number): FilterDoc {
  const addr = pickAddress(s);
  return {
    idHint: String(s.custom_id ?? s.customer_number ?? i),
    customerName: String(s.customer_name ?? '').trim(),
    city: String(addr?.city ?? '').trim(),
    postalCode: String(addr?.postal_code ?? '').trim(),
    street: [addr?.street, addr?.house_number].filter(Boolean).join(' ').trim(),
    priorityNum: s.priority != null ? Number(s.priority) : null,
    type: String(s.type ?? '').toLowerCase(),
    loadClasses: getLoadClassesText(s),
    loadsTotal: sumLoads(s),
    category: String(s.category ?? '').toLowerCase(),
    reason: String(s.reason ?? '').toLowerCase(),
  };
}

/* ---------- Fields & Ops ---------- */
const FIELDS: Field[] = [
  { name: 'customerName', label: 'Name', type: 'text' },
  { name: 'city', label: 'Stadt', type: 'text' },
  { name: 'postalCode', label: 'PLZ', type: 'text' },
  { name: 'street', label: 'Straße', type: 'text' },
  { name: 'priorityNum', label: 'Priorität', type: 'number' },
  { name: 'type', label: 'Typ', type: 'text' },
  { name: 'loadClasses', label: 'Ladungsart', type: 'text' },
  { name: 'loadsTotal', label: 'Lademenge', type: 'number' },
  { name: 'category', label: 'Category', type: 'text' },
  { name: 'reason', label: 'Reason', type: 'text' },
];

const TEXT_OPS = [
  { id: 'contains', label: 'enthält' },
  { id: 'doesNotContain', label: 'enthält nicht' },
  { id: '=', label: 'ist gleich' },
  { id: '!=', label: 'ist ungleich' },
  { id: 'beginsWith', label: 'beginnt mit' },
  { id: 'endsWith', label: 'endet mit' },
  { id: 'isEmpty', label: 'ist leer' },
  { id: 'isNotEmpty', label: 'ist nicht leer' },
  { id: 'in', label: 'ist einer der Werte' },
];
const NUMBER_OPS = [
  { id: '=', label: 'ist gleich' },
  { id: '!=', label: 'ist ungleich' },
  { id: '>', label: 'größer als' },
  { id: '>=', label: 'größer/gleich' },
  { id: '<', label: 'kleiner als' },
  { id: '<=', label: 'kleiner/gleich' },
  { id: 'between', label: 'zwischen' },
  { id: 'isEmpty', label: 'ist leer' },
  { id: 'isNotEmpty', label: 'ist nicht leer' },
] as const;

/* ---------- json-logic ops ---------- */
let OPS_ADDED = false;
function ensureJsonLogicOps() {
  if (OPS_ADDED) return;
  OPS_ADDED = true;
  jsonLogic.add_operation('startsWith', (a: any, b: any) =>
    String(a ?? '').toLowerCase().startsWith(String(b ?? '').toLowerCase())
  );
  jsonLogic.add_operation('endsWith', (a: any, b: any) =>
    String(a ?? '').toLowerCase().endsWith(String(b ?? '').toLowerCase())
  );
  jsonLogic.add_operation('icontains', (a: any, b: any) =>
    String(a ?? '').toLowerCase().includes(String(b ?? '').toLowerCase())
  );
  jsonLogic.add_operation('empty', (x: any) =>
    x == null || (typeof x === 'string' && x.trim() === '') || (Array.isArray(x) && x.length === 0)
  );
}
ensureJsonLogicOps();

/* ---------- DRAG wrapper ---------- */
const DraggableItem: React.FC<{
  dragId: string;
  ids: string[];
  children: (
    args: {
      setNodeRef: (el: HTMLElement | null) => void;
      attributes: any;
      listeners: any;
      style: React.CSSProperties;
    }
  ) => React.ReactNode;
}> = ({ dragId, ids, children }) => {
  const { setNodeRef, attributes, listeners, transform } = useDraggable({
    id: dragId,
    data: { type: 'unplanned', ids },
  });
  const style: React.CSSProperties = React.useMemo(() => {
    if (transform) {
      return {
        position: 'relative',
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        touchAction: 'none',
        cursor: 'grabbing',
        zIndex: 4000,
      };
    }
    return { touchAction: 'none', cursor: 'grab' };
  }, [transform]);
  return <>{children({ setNodeRef, attributes, listeners, style })}</>;
};

/* ---------- Component ---------- */
const UnplannedShipmentsTimeline: React.FC<UnplannedShipmentsTimelineProps> = React.memo(
  ({ shipments, title = 'Unplanned Shipments', maxHeight = 704, onSelect, onClose, dimensionLabels,
    onFilteringChange, onAssignShipments, routeIds = [], onReadyToAssign,
    routesHidden = false, dragEnabled = false, assignedIds = new Set() }) => {
    const classes = useStyles({ maxHeight, clickable: !!onSelect });

    const totalCount = shipments?.length ?? 0;
    const docs = React.useMemo(() => shipments.map(toFilterDoc), [shipments]);

    /* Search (Fuse.js) */
    const [q, setQ] = React.useState('');
    const qDeb = useDebounced(q, 250);
    const fuse = React.useMemo(
      () => new Fuse<FilterDoc>(docs, {
        keys: ['customerName', 'city', 'street', 'postalCode'],
        threshold: 0.33,
        ignoreLocation: true,
      }),
      [docs]
    );
    const searchIndexSet = React.useMemo(() => {
      if (!qDeb.trim()) return null;
      return new Set(fuse.search(qDeb).map(r => r.refIndex));
    }, [fuse, qDeb]);

    /* Rules (local builder) */
    const [ruleGroup, setRuleGroup] = React.useState<RuleGroupType>({ combinator: 'and', rules: [] });

    /* Popover state */
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const openPopover = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const closePopover = () => setAnchorEl(null);

    const [fieldName, setFieldName] = React.useState<string>('customerName');
    const activeField = React.useMemo(() => FIELDS.find(f => f.name === fieldName)!, [fieldName]);
    const opsForField = activeField?.type === 'number' ? NUMBER_OPS : TEXT_OPS;

    const [operator, setOperator] = React.useState<string>(opsForField[0].id);
    const [value1, setValue1] = React.useState<string>('');
    const [value2, setValue2] = React.useState<string>('');

    React.useEffect(() => {
      setOperator((activeField?.type === 'number' ? NUMBER_OPS : TEXT_OPS)[0].id);
      setValue1(''); setValue2('');
    }, [activeField?.type]);

    const addRule = () => {
      const op = operator;
      let value: any = value1;

      if (op === 'between') value = [Number(value1), Number(value2)];
      else if (op === 'in') value = value1.split(',').map(s => s.trim()).filter(Boolean);
      else if (activeField?.type === 'number' && value1 !== '') value = Number(value1);

      const newRule = { field: fieldName, operator: op, value };
      setRuleGroup(rg => ({ ...rg, rules: [...rg.rules, newRule as any] }));
      setValue1(''); setValue2(''); closePopover();
    };

    const removeRuleAt = (i: number) =>
      setRuleGroup(rg => ({ ...rg, rules: rg.rules.filter((_, idx) => idx !== i) }));

    const clearFilters = () => setRuleGroup({ combinator: 'and', rules: [] });

    /* json-logic from rules */
    const logic: RulesLogic | null = React.useMemo(() => {
      if (!ruleGroup.rules.length) return null;

      const toJL = (r: any) => {
        const v = r.value;
        switch (r.operator) {
          case 'contains': return { icontains: [{ var: r.field }, v] } as any;
          case 'doesNotContain': return { '!': { icontains: [{ var: r.field }, v] } } as any;
          case 'beginsWith': return { startsWith: [{ var: r.field }, v] } as any;
          case 'endsWith': return { endsWith: [{ var: r.field }, v] } as any;
          case 'isEmpty': return { empty: [{ var: r.field }] } as any;
          case 'isNotEmpty': return { '!': { empty: [{ var: r.field }] } } as any;
          case 'in': return { in: [{ var: r.field }, Array.isArray(v) ? v : [v]] };
          case 'between': {
            const a = Number(v?.[0]); const b = Number(v?.[1]);
            const lo = Math.min(a, b); const hi = Math.max(a, b);
            return { and: [{ '>=': [{ var: r.field }, lo] }, { '<=': [{ var: r.field }, hi] }] };
          }
          case '=':
          case '!=':
          case '>':
          case '>=':
          case '<':
          case '<=': return { [r.operator]: [{ var: r.field }, v] } as any;
          default: return null;
        }
      };

      const rules = (ruleGroup.rules as any[]).map(toJL).filter(Boolean);
      if (!rules.length) return null;
      return ruleGroup.combinator === 'or' ? ({ or: rules } as any) : ({ and: rules } as any);
    }, [ruleGroup]);

    /* Apply search + filters */
    const filteredIndexes = React.useMemo(() => {
      const idxs: number[] = [];
      docs.forEach((d, i) => {
        const passSearch = !searchIndexSet || searchIndexSet.has(i);
        const passRules = !logic || jsonLogic.apply(logic as any, d);
        const notAssigned = !assignedIds.has(String(d.idHint));
        if (passSearch && passRules && notAssigned) {
          idxs.push(i);
        }
      });
      return idxs;
    }, [docs, searchIndexSet, logic, assignedIds]);

    const filteredShipments = React.useMemo(
      () => filteredIndexes.map(i => shipments[i]),
      [filteredIndexes, shipments]
    );

    const count = filteredIndexes.length;

    /* notify parent */
    const filteringActive =
      Boolean(qDeb.trim()) || (ruleGroup.rules?.length ?? 0) > 0 || Boolean(anchorEl);
    React.useEffect(() => {
      onFilteringChange?.(filteringActive, count);
    }, [filteringActive, count, onFilteringChange]);

    /* selection mode */
    const [selectMode, setSelectMode] = React.useState(false);
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
    React.useEffect(() => {
      if (selectedIds.size > 0) {
        onReadyToAssign?.();
      }
    }, [selectedIds.size, onReadyToAssign]);
    const toggleSelectMode = () => setSelectMode(v => !v);
    const isSelected = (id: string) => selectedIds.has(id);
    const toggleSelect = (id: string) =>
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    const selectAllFiltered = () =>
      setSelectedIds(() => {
        const next = new Set<string>();
        filteredIndexes.forEach(i => next.add(docs[i].idHint));
        return next;
      });
    const clearSelection = () => setSelectedIds(new Set());

    // Only selected + visible ids (drag payload)
    const selectedVisibleArray = React.useMemo(() => {
      const arr: string[] = [];
      filteredIndexes.forEach(i => {
        const id = docs[i].idHint;
        if (selectedIds.has(id)) arr.push(id);
      });
      return arr;
    }, [filteredIndexes, selectedIds, docs]);
    const selectedVisibleSet = React.useMemo(() => new Set(selectedVisibleArray), [selectedVisibleArray]);

    /* ---------- Context menu + dialog (Add to vehicle) ---------- */
    const [ctxPos, setCtxPos] = React.useState<{ mouseX: number; mouseY: number } | null>(null);
    const [ctxAnchor, setCtxAnchor] = React.useState<null | HTMLElement>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [vehicleChoice, setVehicleChoice] = React.useState<string | null>(null);
    const [contextClickedId, setContextClickedId] = React.useState<string | null>(null);

    const openContextMenu = (e: React.MouseEvent<HTMLElement>, idHint: string) => {
      e.preventDefault();

      const hasAnySelection = selectedIds.size > 0;
      const rowIsSelected = selectedIds.has(idHint);
      if (!hasAnySelection || (selectMode && !rowIsSelected)) return;

      setContextClickedId(idHint);
      setCtxAnchor(e.currentTarget);
      setCtxPos({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
      onReadyToAssign?.();
    };

    React.useEffect(() => {
      if (selectedIds.size === 0 && ctxPos) {
        setCtxPos(null);
      }
    }, [selectedIds.size, ctxPos]);

    const closeContextMenu = () => {
      setCtxPos(null);
      setCtxAnchor(null);
    };

    const handleAddToVehicle = () => {
      closeContextMenu();
      setVehicleChoice(null);
      setDialogOpen(true);
    };

    const handleDialogAssign = () => {
      const ids = new Set<string>();
      // if user had selection & the right-clicked row was part of it, assign all selected+visible
      if (selectedVisibleSet.size > 0 && (contextClickedId && selectedVisibleSet.has(contextClickedId))) {
        selectedVisibleSet.forEach(id => ids.add(id));
      } else if (contextClickedId) {
        ids.add(contextClickedId);
      }
      const finalIds = Array.from(ids);
      if (finalIds.length && vehicleChoice && onAssignShipments) {
        onAssignShipments(finalIds, vehicleChoice);
      }
      setDialogOpen(false);
      setVehicleChoice(null);
      setContextClickedId(null);
    };

    const handleDialogClose = () => {
      setDialogOpen(false);
      setVehicleChoice(null);
    };

    return (
      <Box className={classes.root}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="subtitle2" color="textSecondary" className={classes.grow}>
            {title} ({count} / {totalCount})
          </Typography>
          {onClose && (
            <IconButton size="small" aria-label="close" onClick={onClose}>
              <CloseOutlined fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Controls */}
        <Box className={classes.controls}>
          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ('')} aria-label="clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          {/* Filter trigger */}
          <Tooltip title="Filter">
            <IconButton aria-label="filters" onClick={openPopover}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          {/* Selection toggle */}
          <Tooltip title={selectMode ? 'Done selecting' : 'Select'}>
            <IconButton aria-label="toggle-select" onClick={toggleSelectMode}>
              {selectMode ? <DoneAllIcon /> : <CheckBoxOutlinedIcon />}
            </IconButton>
          </Tooltip>

          {/* Filter popover */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, minWidth: 420 }}>
              <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Spalten</InputLabel>
                  <Select
                    label="Spalten"
                    value={fieldName}
                    onChange={(e) => setFieldName(String(e.target.value))}
                  >
                    {FIELDS.map(f => (
                      <MenuItem key={f.name} value={f.name}>{String(f.label)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Operatoren</InputLabel>
                  <Select
                    label="Operatoren"
                    value={operator}
                    onChange={(e) => setOperator(String(e.target.value))}
                  >
                    {(opsForField).map(op => (
                      <MenuItem key={op.id} value={op.id}>{op.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Value */}
                {operator === 'between' ? (
                  <Stack direction="row" spacing={1} sx={{ minWidth: 200 }}>
                    <TextField
                      size="small"
                      label="von"
                      value={value1}
                      onChange={(e) => setValue1(e.target.value)}
                      type={activeField?.type === 'number' ? 'number' : 'text'}
                    />
                    <TextField
                      size="small"
                      label="bis"
                      value={value2}
                      onChange={(e) => setValue2(e.target.value)}
                      type={activeField?.type === 'number' ? 'number' : 'text'}
                    />
                  </Stack>
                ) : operator === 'isEmpty' || operator === 'isNotEmpty' ? (
                  <TextField size="small" label="Wert" value="" disabled />
                ) : (
                  <TextField
                    size="small"
                    label={operator === 'in' ? 'Werte (comma getrennt)' : 'Wert'}
                    placeholder={operator === 'in' ? 'dropped, invalid' : 'Wert filtern'}
                    value={value1}
                    onChange={(e) => setValue1(e.target.value)}
                    type={activeField?.type === 'number' ? 'number' : 'text'}
                    sx={{ minWidth: 200 }}
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
                <Button onClick={closePopover}>Abbrechen</Button>
                <Button variant="contained" onClick={addRule} disabled={
                  !(operator === 'between' ? value1 !== '' && value2 !== '' :
                    (operator === 'isEmpty' || operator === 'isNotEmpty') ? true : value1 !== '')
                }>
                  Filter hinzufügen
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Box>

        {/* Active filter chips */}
        {ruleGroup.rules.length > 0 && (
          <Stack direction="row" flexWrap="wrap" className={classes.chipsRow}>
            {ruleGroup.rules.map((r: any, i) => {
              const fld = FIELDS.find(f => f.name === r.field);
              const label = `${fld?.label} ${r.operator} ${Array.isArray(r.value) ? r.value.join('..') : (r.value ?? '')}`;
              return <Chip key={i} label={label} onDelete={() => removeRuleAt(i)} />;
            })}
            <Button size="small" onClick={clearFilters}>Alle löschen</Button>
          </Stack>
        )}

        {/* Selection bar */}
        {selectMode && (
          <Stack direction="row" spacing={1} alignItems="center" className={classes.chipsRow}>
            <Typography variant="body2">
              {filteredIndexes.filter(i => selectedIds.has(docs[i].idHint)).length} / {filteredIndexes.length} selected
            </Typography>
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={selectAllFiltered}
              disabled={filteredIndexes.length === 0}
            >
              SELECT ALL
            </Button>
            <Button size="small" onClick={clearSelection} disabled={selectedIds.size === 0}>
              CLEAR
            </Button>
          </Stack>
        )}

        <Divider />

        {/* Timeline */}
        <Box className={classes.scroll}>
          {count === 0 ? (
            <Typography variant="body2" className={classes.emptyText}>None</Typography>
          ) : (
            <Timeline position="right" className={classes.timelineRoot}>
              {filteredShipments.map((s, i) => {
                const idx = filteredIndexes[i];
                const idHint = docs[idx].idHint;
                const nameText = getTitle(s, idx);
                const addressText = formatAddress(pickAddress(s)) || 'No address';
                const priorityText = s.priority ?? 'N/A';

                const types = new Set(String(s.type || '').toLowerCase().split(/[,\s+]/).filter(Boolean));
                const isPickup = types.has('pickup');
                const isDelivery = types.has('delivery');

                const loadClassesRaw = getLoadClassesText(s);
                const showLoadClass = loadClassesRaw.length > 0;

                const loads = (Array.isArray(s.loads) ? s.loads : []).map((val, i2) => {
                  const label = dimensionLabels?.[i2]?.dimension || `Load ${i2 + 1}`;
                  const unit = dimensionLabels?.[i2]?.unit || '';
                  return { label, value: val, unit };
                });
                const showLoads = loads.length > 0;

                let dotVariant = classes.dotDefault;
                if (isPickup && isDelivery) dotVariant = classes.dotMixed;
                else if (isPickup) dotVariant = classes.dotPickup;
                else if (isDelivery) dotVariant = classes.dotDelivery;

                const baseCanDrag = selectMode ? selectedVisibleSet.has(idHint) : true;
                const canDrag =
                  dragEnabled &&
                  (!routesHidden) &&
                  baseCanDrag;
                const dragIds = (selectMode && selectedVisibleArray.length > 0)
                  ? selectedVisibleArray
                  : [idHint];

                const baseKey = idHint;

                const Row = (extra: any = {}) => (
                  <TimelineItem
                    {...extra}
                    key={baseKey}
                    className={classes.item}
                    onClick={() => { if (!selectMode) onSelect?.(s, idx); }}
                    onContextMenu={(e) => {
                      const hasAnySelection = selectedIds.size > 0;
                      const rowIsSelected = selectedIds.has(idHint);
                      if (!hasAnySelection || (selectMode && !rowIsSelected)) {
                        e.preventDefault();
                        return;
                      }
                      openContextMenu(e, idHint);
                    }}
                  >
                    <TimelineSeparator
                      sx={{
                        flex: '0 0 auto',
                        display: 'grid',
                        gridAutoFlow: 'column',
                        alignItems: 'baseline',
                        justifyContent: 'start',
                        gap: '14px',
                        minWidth: 'unset',
                        mx: 0,
                        pl: 0,
                      }}
                    >
                      {selectMode && (
                        <Checkbox
                          size="small"
                          sx={{ p: 0, mr: 0 }}
                          checked={isSelected(idHint)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelect(idHint);
                          }}
                        />
                      )}

                      <TimelineDot className={`${classes.dotBase} ${dotVariant}`}>
                        <StopCircleOutlined fontSize="small" className={classes.dotIcon} />
                      </TimelineDot>
                    </TimelineSeparator>

                    <TimelineContent className={classes.content}>
                      <Typography variant="subtitle2" component="div" className={classes.textLine}>
                        <Box component="span" className={classes.textSmall}>{nameText || ''}</Box>
                      </Typography>
                      <Typography variant="subtitle2" component="div" className={classes.textLine}>
                        <Box component="span" className={classes.textSmall}>{addressText || ''}</Box>
                      </Typography>

                      <Typography variant="subtitle2" component="div" className={classes.textLine}>
                        <Box className={classes.priorityRow}>
                          <Box className={classes.priorityItem}>
                            <LowPriorityOutlined className={classes.priorityIcon} />
                            {priorityText ?? ''}
                          </Box>
                          {showLoadClass && (
                            <Box className={classes.priorityItem}>
                              <LocalShippingOutlined className={classes.loadIcon} />
                              {loadClassesRaw}
                            </Box>
                          )}
                        </Box>
                      </Typography>

                      {showLoads && (
                        <Typography variant="subtitle2" component="div" className={classes.textLine}>
                          <Box className={classes.loadsRow}>
                            {loads.map((it, idx2) => (
                              <Box key={idx2} className={classes.loadItem}>
                                <span className={classes.loadLabel}>{it.label}:</span>
                                {it.unit ? (
                                  <Tooltip title={it.unit} arrow>
                                    <span className={classes.loadValue}>{it.value}</span>
                                  </Tooltip>
                                ) : (
                                  <span className={classes.loadValue}>{it.value}</span>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                );

                if (!canDrag) return (
                  <React.Fragment key={baseKey}>
                    {Row()}
                  </React.Fragment>
                );

                return (
                  <DraggableItem key={baseKey} dragId={`unplanned-${idHint}`} ids={dragIds}>
                    {({ setNodeRef, attributes, listeners, style }) => (
                      <div ref={setNodeRef} {...attributes} {...listeners}
                        style={style} onMouseDown={(e) => {
                          if (routesHidden) {
                            e.stopPropagation();
                            onReadyToAssign?.();
                            return;
                          }
                        }}>
                        {Row()}
                      </div>
                    )}
                  </DraggableItem>
                );
              })}
            </Timeline>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          open={Boolean(ctxPos)}
          onClose={closeContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={ctxPos ? { top: ctxPos.mouseY, left: ctxPos.mouseX } : undefined}
          //PaperProps={{ className: classes.ctxPaper }}
          MenuListProps={{ className: classes.ctxList, dense: true, disablePadding: true }}
        >
          <MenuItem
            onClick={handleAddToVehicle}
            className={classes.ctxItem}
            disableRipple
          >
            <span>Add to vehicle</span>
            <KeyboardArrowRightOutlined className={classes.ctxChevron} />
          </MenuItem>
        </Menu>

        {/* Add to vehicle Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogTitle className={classes.dialogTitle}>
            <Box className={classes.titleLeft}>
              <Box className={classes.diamondBox}>
                <WarningAmberOutlined className={classes.warningIcon} />
              </Box>
              <Typography variant="h6" className={classes.titleText}>
                Confirm assignment
              </Typography>
            </Box>
            <IconButton onClick={handleDialogClose} size="small">
              <CloseOutlined />
            </IconButton>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <Autocomplete
              options={routeIds}
              value={vehicleChoice}
              onChange={(_, v) => setVehicleChoice(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle / Route"
                  placeholder="Search vehicle name..."
                  InputLabelProps={{ shrink: true }}
                />
              )}
              autoHighlight
              filterSelectedOptions
              disableClearable={false}
            />
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              Tip: Use selection mode to add multiple visible shipments at once, or drag & drop directly onto a vehicle.
            </Typography>
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <Button variant="outlined" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!vehicleChoice}
              onClick={handleDialogAssign}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

export default UnplannedShipmentsTimeline;
